import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ALL_LOCALES, DEFAULT_LOCALE, localizePath, splitLocale } from "@/lib/locale";
import { getHostTenant } from "@/lib/hostTenant";

/* Fase 13 — capa SEO client-side. Producción sirve HTML renderizado a los
 * bots (dynamic rendering en el edge, verificado 21-jul), por lo que todo lo
 * que fijamos aquí (title, metas, canonical, hreflang, JSON-LD, robots) SÍ
 * llega a los buscadores. Reglas duras:
 *  - canonical SIEMPRE absoluto sobre PUBLIC_BASE (jamás window.location.origin
 *    → evitaría fugas del dominio de preview en HTML público).
 *  - hreflang recíproco + x-default SOLO en páginas de nivel 1 (localized:true).
 *  - robots noindex para borradores/privado/auth/404 (nunca bloqueado por
 *    robots.txt cuando Google necesita VER el noindex). */

export const PUBLIC_BASE = (
  process.env.REACT_APP_PUBLIC_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/$/, "");

/* Fase E1 — bajo un dominio personalizado conectado, canonical/OG/hreflang se
   emiten sobre ese dominio; sin dominio, todo sigue sobre PUBLIC_BASE. */
export const getPublicBase = () =>
  getHostTenant() ? `https://${window.location.hostname}` : PUBLIC_BASE;

const OG_LOCALE = { en: "en_US", es: "es_ES", de: "de_DE", fr: "fr_FR", it: "it_IT" };

/* WS9: toda página pública emite SIEMPRE una imagen social. Si la página no
 * aporta imagen propia (evento→hero, lote/caballo→cover, organizador→logo),
 * cae a la imagen de marca — pública, sin bloqueo de auth. */
const DEFAULT_OG_IMAGE = "/images/brand/og-default.jpg";

function setMeta(attr, key, content) {
  if (!content) return () => {};
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  const prev = tag.getAttribute("content");
  tag.setAttribute("content", content);
  return () => {
    if (created) tag.remove();
    else if (prev != null) tag.setAttribute("content", prev);
  };
}

function setLink(rel, href, extra = {}) {
  if (!href) return () => {};
  const selector = extra.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]`;
  let tag = document.head.querySelector(selector);
  const created = !tag;
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => tag.setAttribute(k, v));
    document.head.appendChild(tag);
  }
  const prev = tag.getAttribute("href");
  tag.setAttribute("href", href);
  return () => {
    if (created) tag.remove();
    else if (prev != null) tag.setAttribute("href", prev);
  };
}

function absolute(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${getPublicBase()}${url.startsWith("/") ? "" : "/"}${url}`;
}

function normalizePath(pathname) {
  const p = (pathname || "/").split("?")[0].split("#")[0];
  return p.length > 1 ? p.replace(/\/+$/, "") : "/";
}

/**
 * Meta por página.
 * @param {object} opts
 *  - title, description: únicos por ruta e idioma.
 *  - image, imageAlt: OG/Twitter (se absolutiza sobre PUBLIC_BASE).
 *  - url: canonical explícito (absoluto) — si no, PUBLIC_BASE + pathname actual.
 *  - robots: p.ej. "noindex, nofollow" para borradores/privado/auth/404.
 *  - localized: true en páginas de NIVEL 1 → emite hreflang recíproco + x-default.
 *  - ogType: "website" (default) | "article" | ...
 */
export function usePageMeta({ title, description, image, imageAlt, url, robots, localized = false, ogType = "website" }) {
  const { i18n } = useTranslation();
  const lng = (i18n.resolvedLanguage || DEFAULT_LOCALE).slice(0, 2);

  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const BASE = getPublicBase();
    const currentPath = normalizePath(window.location.pathname);
    const canonical = url || `${BASE}${currentPath}`;
    const img = absolute(image) || `${BASE}${DEFAULT_OG_IMAGE}`;
    const cleanups = [
      setMeta("name", "description", description),
      setMeta("name", "robots", robots),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:image", img),
      setMeta("property", "og:image:alt", img ? imageAlt || title : null),
      setMeta("property", "og:type", ogType),
      setMeta("property", "og:url", canonical),
      setMeta("property", "og:locale", OG_LOCALE[lng] || OG_LOCALE.en),
      setMeta("name", "twitter:card", img ? "summary_large_image" : "summary"),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description),
      setMeta("name", "twitter:image", img),
      setLink("canonical", canonical),
    ];
    if (localized) {
      const { path } = splitLocale(currentPath);
      for (const l of ALL_LOCALES) {
        cleanups.push(setLink("alternate", `${BASE}${localizePath(path, l)}`, { hreflang: l }));
      }
      cleanups.push(setLink("alternate", `${BASE}${path}`, { hreflang: "x-default" }));
    }
    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, image, imageAlt, url, robots, localized, ogType, lng]);
}

// Inject a Schema.org JSON-LD script for the current page (client-side SEO).
export function useJsonLd(data) {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    if (!json) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = json;
    document.head.appendChild(el);
    return () => el.remove();
  }, [json]);
}
